// ACTIONS: the deleted docs at the foot of Docs, folded under "Deleted" and read when unfolded
// (`GET pages/deleted`: the spaces the caller edits), each with a visible Restore icon (`POST
// pages/{id}/undelete`). A doc deleted while live comes back where it was; one deleted from the
// archive comes back archived. The pages that moved up a level at the delete stay where they are.

import { newIdempotencyKey } from "@companyos/sdk";
import { cn, toast } from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRightIcon, RotateCcwIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { RowAction } from "../settings/row-actions";
import { ago } from "./doc-kinds";
import { invalidateWork, WORK_PAGE, workKeys } from "./work-queries";

const deletedQuery = (api: Api, co: string) =>
  queryOptions({
    // Under the work key: a delete (which refreshes the work lists) refreshes this one too.
    queryKey: [...workKeys.all(co), "pages-deleted"] as const,
    refetchOnMount: "always",
    queryFn: () => api.client.pages.deleted({ params: { co }, query: { limit: WORK_PAGE } }),
  });

export function DeletedDocs({ api, company }: { api: Api; company: CompanyAccess }) {
  const co = company.id;
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const q = useQuery({ ...deletedQuery(api, co), enabled: open });
  const restore = useMutation({
    mutationFn: (d: { id: string; title: string }) =>
      api.client.pages.undelete({
        params: { co, id: d.id },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: (p) =>
      toast.add({
        title: `${p.title} restored`,
        ...(p.archived_at
          ? { description: "It was archived when deleted; it is in Archived." }
          : {}),
        type: "success",
      }),
    onSettled: () => invalidateWork(qc, co),
  });
  const rows = q.data?.data ?? [];
  return (
    <div data-slot="deleted-docs" className="mt-4">
      <button
        type="button"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-md px-2 py-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
        onClick={() => setOpen((o) => !o)}
      >
        <ChevronRightIcon
          aria-hidden="true"
          className={cn("size-3.5 transition-transform", open && "rotate-90")}
        />
        <Trash2Icon aria-hidden="true" className="size-3.5" />
        Deleted{q.data ? ` (${rows.length})` : ""}
      </button>
      {open && q.data && !rows.length ? (
        <p className="py-1 pl-7 text-xs text-muted-foreground">Nothing deleted.</p>
      ) : null}
      {open && rows.length ? (
        <ul aria-label="Deleted docs" className="m-0 flex list-none flex-col p-0">
          {rows.map((d) => (
            <li
              key={d.id}
              data-deleted-doc={d.id}
              className="group/row flex min-h-8 items-center gap-2 rounded-md pl-7 pr-1 text-sm text-muted-foreground hover:bg-muted"
            >
              <span className="min-w-0 flex-1 truncate">{d.title}</span>
              <span className="text-2xs text-faint">deleted {ago(d.deleted_at)}</span>
              <RowAction
                icon={RotateCcwIcon}
                label={`Restore ${d.title}`}
                tip="Restore"
                disabled={restore.isPending}
                onClick={() => restore.mutate(d)}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
